import { FileActionButton } from "../file-action/file-action-controls";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { useState, useMemo, forwardRef, useImperativeHandle } from "react";
import * as XLSX from "xlsx";


interface RMSummarylog {
  rawMaterialName: string;
  inwardQuantity: number;
  outwardQuantity: number;
  adjustQuantity: number;
}


interface SummaryTableProps {
  data: RMSummarylog[];
  loading: boolean;
  keyword: string;
}


export const RawMaterialMovementSummary = forwardRef<{ resetFilters: () => void }, SummaryTableProps>(
  ({ data, loading, keyword }, ref) => {
    // Filter state
    const [rawMaterialFilter, setRawMaterialFilter] = useState<string[]>([]);


    // Sort state
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<1 | -1>(1);


    // Expose reset function to parent component
    useImperativeHandle(ref, () => ({
      resetFilters: () => {
        setRawMaterialFilter([]);
        setSortField(null);
        setSortOrder(1);
      }
    }));


    // Apply filters to get filtered data
    const filteredData = useMemo(() => {
      let filtered = data;


      // Apply keyword search
      if (keyword.trim()) {
        const searchTerm = keyword.toLowerCase();
        filtered = filtered.filter(item =>
          item.rawMaterialName?.toLowerCase().includes(searchTerm) ||
          item.inwardQuantity?.toString().includes(searchTerm) ||
          item.outwardQuantity?.toString().includes(searchTerm) ||
          item.adjustQuantity?.toString().includes(searchTerm)
        );
      }


      if (rawMaterialFilter.length > 0) {
        filtered = filtered.filter(item => rawMaterialFilter.includes(item.rawMaterialName));
      }


      // Apply sorting if a sort field is selected
      if (sortField) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[sortField as keyof RMSummarylog];
          const bVal = b[sortField as keyof RMSummarylog];


          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;


          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortOrder * aVal.localeCompare(bVal);
          }


          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortOrder * (aVal - bVal);
          }


          return 0;
        });
      }


      return filtered;
    }, [data, rawMaterialFilter, keyword, sortField, sortOrder]);


    // Get unique raw materials
    const rawMaterialOptions = useMemo(() => {
      const unique = [...new Set(data.map((item) => item.rawMaterialName))];
      return unique.filter(Boolean).sort();
    }, [data]);


    // Handle sorting
    const handleSort = (field: string) => {
      if (sortField === field) {
        setSortOrder(sortOrder === 1 ? -1 : 1);
      } else {
        setSortField(field);
        setSortOrder(1);
      }
    };


    // Clear all filters
    const clearAllFilters = () => {
      setRawMaterialFilter([]);
      setSortField(null);
      setSortOrder(1);
    };


    // Check if any filters are active
    const hasActiveFilters = rawMaterialFilter.length > 0 || keyword.trim() !== "";


    // Export function to convert data to Excel and download
    const exportToExcel = () => {
      if (filteredData.length === 0) {
        return;
      }

      try {
        const exportData = filteredData.map((item) => ({
          "Raw Material": item.rawMaterialName,
          "Inward": item.inwardQuantity,
          "Outward": item.outwardQuantity,
          "Adjust": item.adjustQuantity,
        }));

        // Create worksheet from JSON
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Format numeric columns as numbers with decimals
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        for (let row = 1; row <= range.e.r; row++) {
          // Inward column (column index 1)
          const inwardCell = XLSX.utils.encode_cell({ r: row, c: 1 });
          if (worksheet[inwardCell]) {
            worksheet[inwardCell].t = "n";
            worksheet[inwardCell].z = "#,##0.00";
          }

          // Outward column (column index 2)
          const outwardCell = XLSX.utils.encode_cell({ r: row, c: 2 });
          if (worksheet[outwardCell]) {
            worksheet[outwardCell].t = "n";
            worksheet[outwardCell].z = "#,##0.00";
          }

          // Adjust column (column index 3)
          const adjustCell = XLSX.utils.encode_cell({ r: row, c: 3 });
          if (worksheet[adjustCell]) {
            worksheet[adjustCell].t = "n";
            worksheet[adjustCell].z = "#,##0.00";
          }
        }

        // Set column widths for better readability
        worksheet["!cols"] = [
          { width: 25 }, // Raw Material
          { width: 18 }, // Inward
          { width: 18 }, // Outward
          { width: 18 }, // Adjust
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

        const date = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `raw_material_summary_${date}.xlsx`);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
      }
    };


    // Filterable column header component
    const FilterableColumnHeader = ({
      title,
      filterValue,
      onFilterChange,
      options,
      sortField: columnField,
      showSort = true
    }: {
      title: string;
      filterValue: string[];
      onFilterChange: (value: string[]) => void;
      options: string[];
      sortField?: string;
      showSort?: boolean;
    }) => {
      const isCurrentlySorted = sortField === columnField;
      const activeFilterCount = filterValue.length;


      return (
        <div className="flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="font-semibold">{title}</span>


          <div className="flex align-items-center gap-1">
            {/* Filter dropdown */}
            <div className="p-overlay-badge">
              <MultiSelect
                value={filterValue}
                options={options}
                onChange={(e) => onFilterChange(e.value)}
                placeholder={`Filter ${title}`}
                maxSelectedLabels={0}
                selectedItemsLabel={`${activeFilterCount} selected`}
                className="custom-multiselect"
                style={{
                  width: '40px',
                  height: '32px',
                }}
                panelStyle={{ minWidth: '200px' }}
                display="chip"
                pt={{
                  root: {
                    style: {
                      border: activeFilterCount > 0 ? '2px solid #22c55e' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: activeFilterCount > 0 ? '#f0fdf4' : 'white',
                      transition: 'all 0.3s ease',
                    }
                  },
                  trigger: {
                    style: {
                      width: '100%',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  },
                  label: {
                    style: { display: 'none' }
                  }
                }}
              >
                <template>
                  <div className="flex align-items-center justify-content-center" style={{ width: '100%' }}>
                    <i
                      className="pi pi-filter"
                      style={{
                        fontSize: '0.9rem',
                        color: activeFilterCount > 0 ? '#22c55e' : '#6b7280'
                      }}
                    />
                    {activeFilterCount > 0 && (
                      <span
                        className="ml-1 badge-custom"
                        style={{
                          background: '#22c55e',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '0 5px',
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          minWidth: '16px',
                          textAlign: 'center'
                        }}
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                </template>
              </MultiSelect>
            </div>


            {/* Sort buttons */}
            {showSort && columnField && (
              <Button
                icon={isCurrentlySorted && sortOrder === -1 ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up"}
                className="p-button-text p-button-sm"
                onClick={() => handleSort(columnField)}
                style={{
                  color: isCurrentlySorted ? '#22c55e' : '#6b7280',
                  padding: '4px 8px',
                  minWidth: 'auto',
                  transition: 'all 0.3s ease'
                }}
                tooltip={`Sort ${isCurrentlySorted ? (sortOrder === 1 ? 'Descending' : 'Ascending') : 'Ascending'}`}
                tooltipOptions={{ position: 'top' }}
              />
            )}
          </div>
        </div>
      );
    };


    return (
      <div className="col-12">
        {/* Section Header */}
        <div className="flex align-items-center gap-2 mb-3 px-2">
          <div style={{
            backgroundColor: 'rgb(76, 175, 80)',
            borderRadius: '8px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="pi pi-chart-bar" style={{ color: 'white', fontSize: '1.2rem' }}></i>
          </div>
          <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '1.1rem' }}>Summary View</span>
        </div>


        <div className="card p-2" style={{ borderRadius: '12px' }}>
          {/* Header Actions: Export and Clear Filters */}
          {(filteredData.length > 0 || hasActiveFilters) && (
            <div className="flex justify-content-end mb-2 gap-2">
              {/* Export Button */}
              {filteredData.length > 0 && (
                <FileActionButton action="export"
                  label="Export"
                  icon="pi pi-file-excel"
                  className="export-btn-summary-tooltip"
                  onClick={exportToExcel}
                  style={{
                    backgroundColor: '#22c55e',
                    border: 'none',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#16a34a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#22c55e';
                  }}
                  tooltip={`Export ${filteredData.length} record${filteredData.length !== 1 ? 's' : ''} to Excel`}
                  tooltipOptions={{ position: 'top' }}
                />
              )}


              {/* Clear All Filters Button */}
              {hasActiveFilters && (
                <Button
                  label="Clear All Filters"
                  icon="pi pi-filter-slash"
                  className="p-button-outlined p-button-sm"
                  onClick={clearAllFilters}
                  style={{
                    borderColor: '#22c55e',
                    color: '#22c55e',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22c55e';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#22c55e';
                  }}
                />
              )}
            </div>
          )}


          <DataTable
            value={filteredData}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            emptyMessage="No records found"
            stripedRows
            className="raw-material-summary-table"
          >
            <Column
              field="rawMaterialName"
              header={
                <FilterableColumnHeader
                  title="Raw Material"
                  filterValue={rawMaterialFilter}
                  onFilterChange={setRawMaterialFilter}
                  options={rawMaterialOptions}
                  sortField="rawMaterialName"
                />
              }
              style={{ minWidth: '14rem' }}
            />


            <Column
              field="inwardQuantity"
              header="Inward"
              sortable
              style={{ minWidth: '8rem' }}
            />


            <Column
              field="outwardQuantity"
              header="Outward"
              sortable
              style={{ minWidth: '8rem' }}
            />


            <Column
              field="adjustQuantity"
              header="Adjust"
              sortable
              style={{ minWidth: '8rem' }}
            />
          </DataTable>


          <style>{`
          .custom-multiselect .p-multiselect-trigger {
            padding: 0 !important;
          }
          
          .custom-multiselect .p-multiselect-label {
            display: none !important;
          }


          .raw-material-summary-table .p-datatable-thead > tr > th {
            background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
            border-bottom: 2px solid #e5e7eb;
            padding: 1rem 0.75rem;
          }


          .raw-material-summary-table .p-datatable-thead > tr > th:hover {
            background: linear-gradient(to bottom, #f0fdf4, #dcfce7);
            transition: background 0.3s ease;
          }


          .raw-material-summary-table .p-multiselect-panel .p-multiselect-header {
            background: #f0fdf4;
            border-bottom: 2px solid #22c55e;
          }


          .raw-material-summary-table .p-multiselect-panel .p-multiselect-item:hover {
            background: #dcfce7;
          }


          .raw-material-summary-table .p-multiselect-panel .p-multiselect-item.p-highlight {
            background: #22c55e;
            color: white;
          }


          .raw-material-summary-table .p-button:hover {
            transform: scale(1.1);
            transition: transform 0.2s ease;
          }
        `}</style>
        </div>
      </div>
    );
  }
);


RawMaterialMovementSummary.displayName = 'RawMaterialMovementSummary';
